import { MAX_ATTEMPTS } from '@familyhub/shared';
import type { WordleGameRecord } from './db';

/**
 * Streaks and the guess distribution, **derived** from a player's finished games
 * — the same "computed, never stored" choice the agenda change log and the
 * groceries activity feed make. Nothing here is persisted; it is recomputed from
 * the synced rows on every render.
 */

export interface WordleStats {
  played: number;
  wins: number;
  winRate: number;
  currentStreak: number;
  maxStreak: number;
  /** Mean guesses over won games, to one decimal; null with no wins. The
   *  head-to-head skill metric — lower is sharper. */
  averageGuesses: number | null;
  /** Wins bucketed by attempts used: index 0 = solved in 1, … index 5 = in 6. */
  distribution: number[];
}

/** A `YYYY-MM-DD` date as a whole-day number, for consecutive-day checks. */
function dayNumber(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return Math.floor(Date.UTC(y!, m! - 1, d!) / 86_400_000);
}

export function computeStats(games: WordleGameRecord[]): WordleStats {
  const finished = games
    .filter((g) => !g.deleted_at && (g.status === 'won' || g.status === 'lost'))
    .sort((a, b) => dayNumber(a.puzzle_date) - dayNumber(b.puzzle_date));

  const played = finished.length;
  const wins = finished.filter((g) => g.status === 'won').length;

  const distribution = Array.from({ length: MAX_ATTEMPTS }, () => 0);
  let winGuessTotal = 0;
  for (const g of finished) {
    if (g.status === 'won') {
      const n = g.guesses.length;
      winGuessTotal += n;
      if (n >= 1 && n <= MAX_ATTEMPTS) distribution[n - 1] = (distribution[n - 1] ?? 0) + 1;
    }
  }
  const averageGuesses = wins ? Math.round((winGuessTotal / wins) * 10) / 10 : null;

  // Longest run of consecutive-date wins anywhere in the history.
  let maxStreak = 0;
  let run = 0;
  let prevDay: number | null = null;
  for (const g of finished) {
    const day = dayNumber(g.puzzle_date);
    if (g.status === 'won') {
      run = prevDay !== null && day === prevDay + 1 ? run + 1 : 1;
      maxStreak = Math.max(maxStreak, run);
    } else {
      run = 0;
    }
    prevDay = day;
  }

  // The trailing run of consecutive-date wins ending at the most recent game.
  let currentStreak = 0;
  let expected: number | null = null;
  for (let i = finished.length - 1; i >= 0; i -= 1) {
    const g = finished[i]!;
    if (g.status !== 'won') break;
    const day = dayNumber(g.puzzle_date);
    if (expected === null || day === expected) {
      currentStreak += 1;
      expected = day - 1;
    } else {
      break;
    }
  }

  return {
    played,
    wins,
    winRate: played ? Math.round((wins / played) * 100) : 0,
    currentStreak,
    maxStreak,
    averageGuesses,
    distribution,
  };
}
